import React, { useEffect, useState } from "react";
import { Check, Copy, Link2 } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import Button from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import useGlobalStore from "@/store/globalStore";
import useShareLinkStore from "../../store/useShareLinkStore";

interface SharePreviewButtonProps {
  versionId: string | null;
  /** The version's status; links exist only from "briefed" onwards. */
  status: string;
  /** Matches the status bar's text colour. */
  color: string;
}

/**
 * "Share preview" in the Ad Builder status bar: create, copy or turn off a
 * public link that shows only this version's final ad.
 */
const SharePreviewButton: React.FC<SharePreviewButtonProps> = ({ versionId, status, color }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { state, isBusy, fetchShareLink, createShareLink, revokeShareLink } = useShareLinkStore(
    useShallow((s) => ({
      state: versionId ? s.byVersion[versionId] : undefined,
      isBusy: s.isBusy,
      fetchShareLink: s.fetchShareLink,
      createShareLink: s.createShareLink,
      revokeShareLink: s.revokeShareLink,
    })),
  );
  const { showError } = useGlobalStore(useShallow((s) => ({ showError: s.showError })));

  const isDraft = status === "draft";
  const url = state?.share_link ? `${window.location.origin}${state.share_link.path}` : null;

  useEffect(() => {
    if (open && versionId) fetchShareLink(versionId).catch((e) => showError(e.message));
  }, [open, versionId, fetchShareLink, showError]);

  const run = async (action: () => Promise<void>) => {
    try {
      await action();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Something went wrong");
    }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      showError("Couldn't copy the link. Select it and copy it instead.");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={!versionId || isDraft}
        title={isDraft ? "Brief this asset to share a preview link" : "Share a preview of this ad"}
        style={{ borderColor: "rgba(255,255,255,0.6)", color }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Link2 size={14} />
        Share preview
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share a preview</DialogTitle>
            <DialogDescription>
              Anyone with this link can see this ad exactly as it appears on the canvas.
              They won't see the campaign, other assets, comments or any other details,
              and they don't need to sign in.
            </DialogDescription>
          </DialogHeader>

          {url ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  readOnly
                  value={url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="min-w-0 flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                />
                <Button
                  type="primary"
                  onClick={copy}
                  leadingIcon={copied ? <Check size={16} /> : <Copy size={16} />}
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Opened {state?.share_link?.view_count ?? 0}{" "}
                {state?.share_link?.view_count === 1 ? "time" : "times"}. It shows the
                latest version of this ad and stops working if the asset goes back to draft.
              </p>
              <div className="flex justify-end">
                <Button
                  type="secondary"
                  disabled={isBusy || !versionId}
                  onClick={() => versionId && run(() => revokeShareLink(versionId))}
                >
                  Turn off link
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button
                type="primary"
                isLoading={isBusy}
                disabled={isBusy || !versionId || state?.shareable === false}
                onClick={() => versionId && run(() => createShareLink(versionId))}
              >
                Create link
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SharePreviewButton;
